// Custom types for the application
export type AppRole = 'admin' | 'medico' | 'enfermera' | 'paciente' | 'profesional_independiente';

export type EspecialidadMedica = 
  // Atención de Urgencia
  | 'medicina_general' | 'medico_urgencias' | 'emergencias' | 'paramedico'
  // Médicos Especialistas
  | 'pediatria' | 'cardiologia' | 'traumatologia' | 'neurologia' | 'dermatologia'
  | 'oftalmologia' | 'otorrinolaringologia' | 'urologia' | 'nefrologia'
  | 'gastroenterologia' | 'neumologia' | 'endocrinologia' | 'oncologia'
  | 'cirugia_general' | 'anestesiologia' | 'medicina_interna'
  // Salud Mental
  | 'psicologo_a' | 'psiquiatria'
  // Rehabilitación
  | 'kinesiologo_a' | 'terapeuta_ocupacional' | 'fonoaudiologo_a'
  // Enfermería y Técnicos
  | 'enfermero_a' | 'tecnico_enfermeria'
  // Salud de la Mujer
  | 'ginecologia' | 'matron_a'
  // Odontología (solo consulta, acepta urgencias)
  | 'odontologo_a'
  // Otras
  | 'podologo_a' | 'nutricionista' | 'otra';

export type EstadoCita = 'pendiente' | 'confirmada' | 'en_curso' | 'completada' | 'cancelada' | 'no_asistio';

export type TipoAtencion = 'consultorio' | 'domicilio' | 'telemedicina' | 'urgencias';

export type EstadoSuscripcion = 'activa' | 'pausada' | 'cancelada' | 'trial' | 'vencida';

// Categorías de profesionales
export interface CategoriaProfesional {
  id: string;
  nombre: string;
  descripcion?: string;
  orden: number;
  created_at: string;
}

// Configuración de especialidades
export interface EspecialidadConfig {
  id: string;
  especialidad: string;
  categoria_id?: string;
  nombre_display: string;
  nombre_masculino?: string;
  nombre_femenino?: string;
  permite_24_7: boolean;
  permite_atencion_inmediata: boolean;
  descripcion?: string;
  orden: number;
  created_at: string;
}

export type UserStatus = 'activo' | 'inactivo' | 'bloqueado';

export interface Profile {
  id: string;
  user_id: string;
  nombre: string;
  apellido: string;
  email?: string;
  telefono?: string;
  avatar_url?: string;
  rut?: string;
  fecha_nacimiento?: string;
  genero?: string;
  direccion?: string;
  ciudad?: string;
  region?: string;
  pais?: string;
  codigo_postal?: string;
  latitud?: number;
  longitud?: number;
  documento_identidad?: string;
  rol: string;
  estado: UserStatus;
  verificado: boolean;
  created_at: string;
  updated_at: string;
}

export interface UserRole {
  id: string;
  user_id: string;
  role: AppRole;
  created_at: string;
}

export interface Profesional {
  id: string;
  user_id: string;
  especialidad: EspecialidadMedica;
  numero_registro: string;
  titulo?: string;
  institucion_titulo?: string;
  anos_experiencia?: number;
  disponible_reemplazos?: boolean;
  disponible_domicilio?: boolean;
  disponible_telemedicina?: boolean;
  radio_atencion_km?: number;
  tarifa_consulta?: number;
  tarifa_domicilio?: number;
  tarifa_telemedicina?: number;
  biografia?: string;
  verificado?: boolean;
  rating_promedio?: number;
  total_consultas?: number;
  atencion_24_7?: boolean;
  atencion_inmediata?: boolean;
  created_at: string;
  updated_at: string;
}

export interface Paciente {
  id: string;
  user_id: string;
  grupo_sanguineo?: string;
  alergias?: string[];
  enfermedades_cronicas?: string[];
  medicamentos_actuales?: string[];
  contacto_emergencia_nombre?: string;
  contacto_emergencia_telefono?: string;
  contacto_emergencia_relacion?: string;
  seguro_medico?: string;
  numero_poliza?: string;
  created_at: string;
  updated_at: string;
}

export interface Cita {
  id: string;
  paciente_id: string;
  profesional_id: string;
  tipo_atencion: TipoAtencion;
  fecha_hora: string;
  duracion_minutos?: number;
  estado: EstadoCita;
  motivo_consulta?: string;
  notas?: string;
  direccion_atencion?: string;
  latitud_atencion?: number;
  longitud_atencion?: number;
  precio?: number;
  created_at: string;
  updated_at: string;
}

export interface HistorialClinico {
  id: string;
  paciente_id: string;
  profesional_id?: string;
  cita_id?: string;
  fecha: string;
  diagnostico?: string;
  tratamiento?: string;
  receta?: string;
  notas_medicas?: string;
  signos_vitales?: Record<string, unknown>;
  adjuntos?: string[];
  created_at: string;
}

export interface Suscripcion {
  id: string;
  user_id: string;
  plan: string;
  estado: EstadoSuscripcion;
  precio_mensual?: number;
  fecha_inicio: string;
  fecha_fin?: string;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  created_at: string;
  updated_at: string;
}

export interface Notificacion {
  id: string;
  user_id: string;
  titulo: string;
  mensaje: string;
  tipo?: string;
  leida?: boolean;
  enlace?: string;
  created_at: string;
}

export interface Colaboracion {
  id: string;
  profesional_solicitante_id: string;
  profesional_reemplazo_id?: string;
  fecha_inicio: string;
  fecha_fin: string;
  motivo?: string;
  estado?: string;
  tarifa_propuesta?: number;
  descripcion?: string;
  created_at: string;
  updated_at: string;
}

// Role display helpers
export const roleLabels: Record<AppRole, string> = {
  admin: 'Administrador',
  medico: 'Médico',
  enfermera: 'Enfermera',
  paciente: 'Paciente',
  profesional_independiente: 'Profesional Independiente',
};

export const roleColors: Record<AppRole, string> = {
  admin: 'badge-admin',
  medico: 'badge-medico',
  enfermera: 'badge-enfermera',
  paciente: 'badge-paciente',
  profesional_independiente: 'badge-admin',
};

// Categorías de especialidades organizadas
export const categoriasEspecialidades = {
  'Atención de Urgencia': {
    descripcion: 'Profesionales que pueden atender emergencias y urgencias médicas',
    permite_24_7: true,
    especialidades: ['medicina_general', 'medico_urgencias', 'emergencias', 'paramedico']
  },
  'Médicos Especialistas': {
    descripcion: 'Médicos con especialización en áreas específicas',
    permite_24_7: false,
    especialidades: ['pediatria', 'cardiologia', 'traumatologia', 'neurologia', 'dermatologia', 
      'oftalmologia', 'otorrinolaringologia', 'urologia', 'nefrologia', 'gastroenterologia',
      'neumologia', 'endocrinologia', 'oncologia', 'cirugia_general', 'anestesiologia', 'medicina_interna']
  },
  'Salud Mental': {
    descripcion: 'Profesionales de salud mental y bienestar psicológico',
    permite_24_7: false,
    especialidades: ['psicologo_a', 'psiquiatria']
  },
  'Rehabilitación': {
    descripcion: 'Profesionales de rehabilitación y terapia física',
    permite_24_7: false,
    especialidades: ['kinesiologo_a', 'terapeuta_ocupacional', 'fonoaudiologo_a']
  },
  'Enfermería y Técnicos': {
    descripcion: 'Personal de enfermería y técnicos de salud',
    permite_24_7: true,
    especialidades: ['enfermero_a', 'tecnico_enfermeria']
  },
  'Salud de la Mujer': {
    descripcion: 'Especialistas en salud femenina y maternidad',
    permite_24_7: false,
    especialidades: ['ginecologia', 'matron_a']
  },
  'Odontología': {
    descripcion: 'Atención dental general y urgencias. Solo en consulta, no a domicilio.',
    permite_24_7: false,
    soloConsulta: true,
    especialidades: ['odontologo_a']
  },
  'Otras Especialidades': {
    descripcion: 'Otros profesionales de la salud',
    permite_24_7: false,
    especialidades: ['podologo_a', 'nutricionista', 'otra']
  }
} as const;

// Especialidades que pueden ofrecer atención 24/7
export const especialidades24_7: EspecialidadMedica[] = [
  'medicina_general', 'medico_urgencias', 'emergencias', 'paramedico',
  'enfermero_a', 'tecnico_enfermeria', 'matron_a'
];

export const especialidadLabels: Record<EspecialidadMedica, { display: string; masculino: string; femenino: string }> = {
  // Atención de Urgencia
  medicina_general: { display: 'Médico(a) General', masculino: 'Médico General', femenino: 'Médica General' },
  medico_urgencias: { display: 'Médico(a) de Urgencias', masculino: 'Médico de Urgencias', femenino: 'Médica de Urgencias' },
  emergencias: { display: 'Emergenciólogo(a)', masculino: 'Emergenciólogo', femenino: 'Emergencióloga' },
  paramedico: { display: 'Paramédico(a)', masculino: 'Paramédico', femenino: 'Paramédica' },
  
  // Médicos Especialistas
  pediatria: { display: 'Pediatra', masculino: 'Pediatra', femenino: 'Pediatra' },
  cardiologia: { display: 'Cardiólogo(a)', masculino: 'Cardiólogo', femenino: 'Cardióloga' },
  traumatologia: { display: 'Traumatólogo(a)', masculino: 'Traumatólogo', femenino: 'Traumatóloga' },
  neurologia: { display: 'Neurólogo(a)', masculino: 'Neurólogo', femenino: 'Neuróloga' },
  dermatologia: { display: 'Dermatólogo(a)', masculino: 'Dermatólogo', femenino: 'Dermatóloga' },
  oftalmologia: { display: 'Oftalmólogo(a)', masculino: 'Oftalmólogo', femenino: 'Oftalmóloga' },
  otorrinolaringologia: { display: 'Otorrinolaringólogo(a)', masculino: 'Otorrinolaringólogo', femenino: 'Otorrinolaringóloga' },
  urologia: { display: 'Urólogo(a)', masculino: 'Urólogo', femenino: 'Uróloga' },
  nefrologia: { display: 'Nefrólogo(a)', masculino: 'Nefrólogo', femenino: 'Nefróloga' },
  gastroenterologia: { display: 'Gastroenterólogo(a)', masculino: 'Gastroenterólogo', femenino: 'Gastroenteróloga' },
  neumologia: { display: 'Neumólogo(a)', masculino: 'Neumólogo', femenino: 'Neumóloga' },
  endocrinologia: { display: 'Endocrinólogo(a)', masculino: 'Endocrinólogo', femenino: 'Endocrinóloga' },
  oncologia: { display: 'Oncólogo(a)', masculino: 'Oncólogo', femenino: 'Oncóloga' },
  cirugia_general: { display: 'Cirujano(a) General', masculino: 'Cirujano General', femenino: 'Cirujana General' },
  anestesiologia: { display: 'Anestesiólogo(a)', masculino: 'Anestesiólogo', femenino: 'Anestesióloga' },
  medicina_interna: { display: 'Internista', masculino: 'Internista', femenino: 'Internista' },
  
  // Salud Mental
  psicologo_a: { display: 'Psicólogo(a)', masculino: 'Psicólogo', femenino: 'Psicóloga' },
  psiquiatria: { display: 'Psiquiatra', masculino: 'Psiquiatra', femenino: 'Psiquiatra' },
  
  // Rehabilitación
  kinesiologo_a: { display: 'Kinesiólogo(a)', masculino: 'Kinesiólogo', femenino: 'Kinesióloga' },
  terapeuta_ocupacional: { display: 'Terapeuta Ocupacional', masculino: 'Terapeuta Ocupacional', femenino: 'Terapeuta Ocupacional' },
  fonoaudiologo_a: { display: 'Fonoaudiólogo(a)', masculino: 'Fonoaudiólogo', femenino: 'Fonoaudióloga' },
  
  // Enfermería y Técnicos
  enfermero_a: { display: 'Enfermero(a)', masculino: 'Enfermero', femenino: 'Enfermera' },
  tecnico_enfermeria: { display: 'Técnico(a) en Enfermería', masculino: 'Técnico en Enfermería', femenino: 'Técnica en Enfermería' },
  
  // Salud de la Mujer
  ginecologia: { display: 'Ginecólogo(a)', masculino: 'Ginecólogo', femenino: 'Ginecóloga' },
  matron_a: { display: 'Matrón(a)', masculino: 'Matrón', femenino: 'Matrona' },
  
  // Odontología
  odontologo_a: { display: 'Odontólogo(a)', masculino: 'Odontólogo', femenino: 'Odontóloga' },
  
  // Otras
  podologo_a: { display: 'Podólogo(a)', masculino: 'Podólogo', femenino: 'Podóloga' },
  nutricionista: { display: 'Nutricionista', masculino: 'Nutricionista', femenino: 'Nutricionista' },
  otra: { display: 'Otro Profesional', masculino: 'Otro Profesional', femenino: 'Otra Profesional' },
};

export const estadoCitaLabels: Record<EstadoCita, string> = {
  pendiente: 'Pendiente',
  confirmada: 'Confirmada',
  en_curso: 'En Curso',
  completada: 'Completada',
  cancelada: 'Cancelada',
  no_asistio: 'No Asistió',
};

export const tipoAtencionLabels: Record<TipoAtencion, string> = {
  consultorio: 'Consultorio',
  domicilio: 'A Domicilio',
  telemedicina: 'Telemedicina',
  urgencias: 'Urgencias',
};
